import React from "react";
import FilterDropdown from "./FilterDropdown";
import "./styles/Pagination.css";

/**
 * Build the visible page list: first, last, the current page and its
 * neighbours, with "…" standing in for each stretch that is skipped.
 */
const buildPageItems = (currentPage, totalPages) => {
  const pages = [];
  for (let page = 0; page < totalPages; page += 1) {
    const isEdge = page === 0 || page === totalPages - 1;
    const isNear = page >= currentPage - 1 && page <= currentPage + 1;
    if (isEdge || isNear) pages.push(page);
  }

  const items = [];
  pages.forEach((page, index) => {
    if (index > 0 && page - pages[index - 1] > 1) {
      items.push({ type: "gap", key: `gap-${page}` });
    }
    items.push({ type: "page", key: page, page });
  });
  return items;
};

/**
 * Pagination - Reusable pagination control
 * @param {Object} props
 * @param {number} props.currentPage - Current page (0-indexed)
 * @param {number} props.pageSize - Items per page
 * @param {number} props.totalCount - Total items (alias: totalItems)
 * @param {number} props.totalPages - Optional explicit page count
 * @param {Array} props.pageSizeOptions - Available page sizes
 * @param {Function} props.onPageChange - Handler for page change
 * @param {Function} props.onPageSizeChange - Handler for page size change
 * @param {Function} props.onPrevPage - Optional dedicated previous handler
 * @param {Function} props.onNextPage - Optional dedicated next handler
 * @param {string} props.info - Info text; derived from the counts when absent
 */
const Pagination = ({
  currentPage,
  pageSize,
  totalCount,
  totalItems,
  totalPages: totalPagesProp,
  pageSizeOptions = [10, 20, 50],
  onPageChange,
  onPageSizeChange,
  onPrevPage,
  onNextPage,
  info,
}) => {
  // Products.js passes totalItems/totalPages while the API keys screen
  // passes totalCount. Accept both: reading only totalCount left the
  // page count as NaN on Products, so no numbered buttons rendered.
  const count = Number(totalCount != null ? totalCount : totalItems) || 0;
  const size = Number(pageSize) || 10;

  const computedPages = Math.ceil(count / size);
  const totalPages = Math.max(
    1,
    Number(totalPagesProp != null ? totalPagesProp : computedPages) || 1,
  );

  const isFirst = currentPage <= 0;
  const isLast = currentPage >= totalPages - 1;

  const rangeStart = count === 0 ? 0 : currentPage * size + 1;
  const rangeEnd = Math.min((currentPage + 1) * size, count);
  const infoText =
    info || `Showing ${rangeStart} to ${rangeEnd} of ${count}`;

  const goPrev = () =>
    onPrevPage ? onPrevPage() : onPageChange(currentPage - 1);
  const goNext = () =>
    onNextPage ? onNextPage() : onPageChange(currentPage + 1);

  return (
    <nav className="pagination" aria-label="Pagination">
      <div className="paginationLeft">
        <span className="paginationInfo">{infoText}</span>
      </div>

      <div className="paginationRight">
        <div className="rowsPerPage">
          <label id="dsRowsPerPageLabel">Rows per page</label>
          <FilterDropdown
            label={String(size)}
            value={size}
            active={false}
            options={pageSizeOptions.map((opt) => ({
              value: opt,
              label: String(opt),
            }))}
            onChange={(e) => onPageSizeChange(e.target.value)}
            align="right"
          />
        </div>

        <div className="paginationButtons">
          <button
            type="button"
            className="paginationBtn prev"
            onClick={goPrev}
            disabled={isFirst}
            aria-label="Previous page"
          >
            ‹
          </button>

          {buildPageItems(currentPage, totalPages).map((item) =>
            item.type === "gap" ? (
              <span key={item.key} className="paginationDots" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                type="button"
                key={item.key}
                className={`paginationBtn ${item.page === currentPage ? "active" : ""}`}
                onClick={() => onPageChange(item.page)}
                aria-label={`Page ${item.page + 1}`}
                aria-current={item.page === currentPage ? "page" : undefined}
              >
                {item.page + 1}
              </button>
            ),
          )}

          <button
            type="button"
            className="paginationBtn next"
            onClick={goNext}
            disabled={isLast}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Pagination;
